/**
 * Google Drive connect — on-demand. We ask for Drive permission ONLY when the user
 * chooses to mount a chat on Drive (never at login). Opens the consent popup, waits
 * for it to close, then confirms the connection.
 */
import { callFunction } from '../utils/apiClient';

export const getGoogleDriveAuthUrl = () =>
  callFunction<{ authUrl: string }>('getGoogleDriveAuthUrl', {});

export const getGoogleDriveStatus = () =>
  callFunction<{ connected: boolean; userEmail?: string }>('getGoogleDriveStatus', {});

/**
 * Open the Drive consent popup and resolve once connected. Throws if the popup is blocked.
 *
 * We DON'T poll `popup.closed`: Cross-Origin-Opener-Policy blocks the opener from reading a
 * cross-origin popup's `.closed` (the COOP warning + 60s hang seen in prod). Instead we poll the
 * backend connection STATUS — resolving the moment Drive reports connected — which is COOP-immune.
 * `popup.closed` is still checked best-effort (wrapped) only to end early if the user cancels.
 */
export async function connectDrive(): Promise<boolean> {
  const { authUrl } = await getGoogleDriveAuthUrl();
  const popup = window.open(authUrl, 'wisetree-drive-connect', 'width=500,height=680');
  if (!popup) throw new Error('Popup blocked — allow popups to connect Google Drive.');

  const deadline = Date.now() + 120_000; // give the user 2 min to consent
  return new Promise<boolean>((resolve) => {
    const timer = setInterval(async () => {
      try {
        const { connected } = await getGoogleDriveStatus();
        if (connected) { clearInterval(timer); try { popup.close(); } catch { /* COOP */ } resolve(true); return; }
      } catch { /* transient — keep polling */ }

      let closed = false;
      try { closed = popup.closed; } catch { /* COOP blocks this — ignore */ }
      if (closed || Date.now() > deadline) {
        clearInterval(timer);
        try { const { connected } = await getGoogleDriveStatus(); resolve(connected); }
        catch { resolve(false); }
      }
    }, 1500);
  });
}
